import type { JSX } from "react";
import PullToRefresh from "react-simple-pull-to-refresh";

import { cn } from "@repo/cn";
import ArrowDown from "@repo/icons/default/ArrowDown";
import { CpsSpinner } from "corpus";

interface PageProps extends React.ComponentPropsWithoutRef<"div"> {
  children: React.ReactNode;
  className?: string;
  onRefresh?: () => Promise<void>;
  showCustomerIoContainer?: boolean;
  sheet?: boolean;
  sheetHeader?: React.ReactNode;
}

const PAGE_CLASSES =
  "flex-1 container m-auto overflow-scroll scrollbar-hidden overscroll-none px-4 pt-4 pb-4 md:max-w-[768px] md:overflow-visible";

const SHEET_PAGE_CLASSES =
  "flex-1 container m-auto overflow-scroll scrollbar-hidden overscroll-none md:max-w-[768px] md:overflow-visible bg-linear-to-b/srgb from-primary-400 from-50% to-white to-50% md:bg-none";

interface PageSheetProps {
  children: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
}

/**
 * Content sheet of the new home layout: the orange strip (and the header content, when given)
 * stays sticky at the top while the rounded white sheet scrolls over it. Once the sheet reaches
 * the top, its rounded cap sticks and the content scrolls under it.
 */
const PageSheet = ({ children, className, header }: PageSheetProps): JSX.Element => (
  <>
    <div className="sticky top-0 pb-4 bg-primary-400 md:static md:pb-0 md:bg-transparent">
      {header ? <div className="px-4 pb-4 md:pt-4 md:pb-0">{header}</div> : null}
    </div>
    <div className="relative z-10 -mt-4 min-h-full bg-white rounded-t-[1.25rem] md:mt-0 md:rounded-none">
      <div className="sticky top-0 z-10 -mb-4 bg-primary-400 md:hidden" aria-hidden>
        <div className="h-4 bg-white rounded-t-[1.25rem]" />
      </div>
      <div className={cn("px-4 pt-4 pb-4", className)}>{children}</div>
    </div>
  </>
);

/**
 * Page component that renders page content with standard page styling.
 *
 * @param children - Page component children.
 * @param className -  Page componente className
 * @param onRefresh - Function to call when the page is refreshed and pull to refresh is triggered.
 * @param showCustomerIoContainer - Flag to determine whether to show the Customer.io container on the page.
 * @param sheet - Renders the content in a rounded sheet that scrolls over the orange top, as in the new home. `className` then styles the sheet.
 * @param sheetHeader - Content kept sticky on the orange top, under the sheet.
 * @param props - Additional props to pass to the page component.
 * @returns JSX.Element - Rendered Page component.
 */
export const Page = ({
  children,
  className,
  onRefresh,
  showCustomerIoContainer = true,
  sheet = false,
  sheetHeader,
  ...props
}: PageProps): JSX.Element => {
  const containerClassName = sheet ? SHEET_PAGE_CLASSES : cn(PAGE_CLASSES, className);
  const wrap = (content: React.ReactNode): React.ReactNode =>
    sheet ? (
      <PageSheet className={className} header={sheetHeader}>
        {content}
      </PageSheet>
    ) : (
      content
    );

  if (onRefresh) {
    return (
      <div className={containerClassName} {...props}>
        <PullToRefresh
          onRefresh={onRefresh}
          refreshingContent={
            <div className="py-4">
              <CpsSpinner small />
            </div>
          }
          pullingContent={
            <div className="flex flex-col w-full items-center justify-center gap-2 text-neutral-400">
              Atualizar
              <ArrowDown className="fill-neutral-400" />
            </div>
          }
          pullDownThreshold={95}
          maxPullDownDistance={100}
        >
          {/** biome-ignore lint/complexity/noUselessFragments: legacy code */}
          <>
            {wrap(children)}
            {showCustomerIoContainer ? <div id="customer-page-component-footer" /> : null}
          </>
        </PullToRefresh>
      </div>
    );
  }

  return (
    <div className={containerClassName} {...props}>
      {wrap(children)}
      {showCustomerIoContainer ? <div id="customer-page-component-footer" /> : null}
    </div>
  );
};

interface PageTitleDescriptionProps extends React.ComponentPropsWithoutRef<"p"> {
  children: React.ReactNode;
  className?: string;
}

export const PageTitle = ({
  children,
  ...props
}: PageTitleDescriptionProps): JSX.Element => {
  return (
    <p
      {...props}
      className={cn(
        "pt-4 text-left text-xl text-neutral-600 font-medium w-full",
        props.className,
      )}
    >
      {children}
    </p>
  );
};

export const PageDescription = ({
  children,
  ...props
}: PageTitleDescriptionProps): JSX.Element => {
  return (
    <p
      {...props}
      className={cn("text-left text-md text-neutral-500 w-full mt-1.5", props.className)}
    >
      {children}
    </p>
  );
};

interface PageSectionProps extends React.ComponentPropsWithoutRef<"div"> {
  children: React.ReactNode;
  className?: string;
}

export const PageSection = ({ children, ...props }: PageSectionProps): JSX.Element => {
  return (
    <div {...props} className={cn("px-2", props.className)}>
      {children}
    </div>
  );
};
